import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { ArrowLeft, ArrowRight, RotateCw, Shuffle, ThumbsUp, X as XIcon, Zap, Clock } from "lucide-react";
import { toast } from "sonner";

const QUALITY_BUTTONS = [
  { label: "Again", quality: 0, tint: "border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white", icon: XIcon, hint: "I forgot" },
  { label: "Hard", quality: 3, tint: "border-brand-linen text-brand-ink hover:bg-brand-linen", icon: Clock, hint: "Answered but shaky" },
  { label: "Good", quality: 4, tint: "border-brand-linen text-brand-ink hover:bg-brand-linen", icon: ThumbsUp, hint: "Got it" },
  { label: "Easy", quality: 5, tint: "border-brand-secondary text-brand-secondary hover:bg-brand-secondary hover:text-white", icon: Zap, hint: "Too easy" },
];

function relativeDays(days) {
  if (days == null) return "";
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 30) return `in ${days} days`;
  if (days < 365) return `in ${Math.round(days / 7)} weeks`;
  return `in ${Math.round(days / 30)} months`;
}

export default function Flashcards() {
  const { slug } = useParams();
  const [cards, setCards] = useState([]);
  const [title, setTitle] = useState("");
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [states, setStates] = useState({}); // { "0": {ease, due_at, interval_days, due, reps, ...} }
  const [dueOnly, setDueOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [cardsRes, stateRes] = await Promise.allSettled([
      api.get(`/courses/${slug}/flashcards`),
      api.get(`/flashcards/${slug}/state`),
    ]);
    if (cardsRes.status === "fulfilled") {
      setCards(cardsRes.value.data.flashcards || []);
      setTitle(cardsRes.value.data.course_title);
    }
    if (stateRes.status === "fulfilled") setStates(stateRes.value.data.states || {});
  };
  useEffect(() => { load(); }, [slug]);

  const orderedIndexes = useMemo(() => {
    // Default: 0..N. If dueOnly, filter to cards not yet seen or due now.
    const all = cards.map((_, idx) => idx);
    if (!dueOnly) return all;
    return all.filter((idx) => {
      const s = states[String(idx)];
      if (!s) return true; // never seen → treat as due
      return !!s.due;
    });
  }, [cards, states, dueOnly]);

  const dueCount = useMemo(() => {
    return cards.reduce((n, _, idx) => {
      const s = states[String(idx)];
      if (!s) return n + 1;
      return s.due ? n + 1 : n;
    }, 0);
  }, [cards, states]);

  if (cards.length === 0) return <Layout><div className="text-brand-muted">Loading flashcards…</div></Layout>;
  if (orderedIndexes.length === 0) {
    return (
      <Layout>
        <Link to={`/courses/${slug}`} className="inline-flex items-center gap-2 text-sm text-brand-secondary hover:text-brand-ink">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to course
        </Link>
        <div className="mt-16 max-w-lg mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">{title} · Flashcards</div>
          <h1 className="font-serif text-4xl text-brand-ink mt-2">You&apos;re all caught up.</h1>
          <p className="text-brand-body mt-3">Nothing due right now. Come back tomorrow or review the full deck.</p>
          <button onClick={() => { setDueOnly(false); setI(0); }}
            className="mt-6 inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover">
            Review the full deck
          </button>
        </div>
      </Layout>
    );
  }

  const cardIdx = orderedIndexes[Math.min(i, orderedIndexes.length - 1)];
  const c = cards[cardIdx];
  const currentState = states[String(cardIdx)];

  const goto = (nextI) => { setFlipped(false); setI(((nextI % orderedIndexes.length) + orderedIndexes.length) % orderedIndexes.length); };
  const prev = () => goto(i - 1);
  const next = () => goto(i + 1);
  const shuffle = () => goto(Math.floor(Math.random() * orderedIndexes.length));

  const rate = async (quality) => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/flashcards/review", {
        course_slug: slug,
        card_index: cardIdx,
        quality,
      });
      setStates((s) => ({ ...s, [String(cardIdx)]: { ...data, due: false } }));
      const msg = quality < 3
        ? "Marked — you'll see this one again today"
        : `Next review ${relativeDays(data.interval_days)}`;
      toast.success(msg);
      // Advance
      next();
    } catch (e) {
      toast.error("Could not save review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Link to={`/courses/${slug}`} className="inline-flex items-center gap-2 text-sm text-brand-secondary hover:text-brand-ink">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to course
      </Link>

      <div className="mt-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">{title} · Flashcards</div>
          <h1 className="font-serif text-4xl text-brand-ink mt-2">Card {i + 1} of {orderedIndexes.length}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="flashcard-due-toggle"
            onClick={() => { setDueOnly(!dueOnly); setI(0); }}
            className={`text-sm rounded-full px-4 py-2 border transition-colors ${dueOnly ? "bg-brand-ink text-brand-bone border-brand-ink" : "border-brand-linen text-brand-ink hover:bg-brand-linen"}`}
          >
            {dueOnly ? "Reviewing due" : `Due today (${dueCount})`}
          </button>
          <button onClick={shuffle} className="inline-flex items-center gap-2 text-sm border border-brand-linen rounded-full px-4 py-2 hover:bg-brand-linen text-brand-ink">
            <Shuffle className="w-4 h-4" strokeWidth={1.5} /> Shuffle
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-col items-center">
        <div className="flip-card w-full max-w-2xl aspect-[4/3]" onClick={() => setFlipped(!flipped)} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setFlipped(!flipped); }}}
          data-testid={T.flashcard}>
          <motion.div className="flip-inner w-full h-full relative"
            animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.6 }}>
            <div className="flip-face absolute inset-0 bg-white border border-brand-linen rounded-3xl shadow-card flex flex-col items-center justify-center p-12 text-center">
              <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">Term</div>
              <div className="font-serif text-5xl sm:text-6xl text-brand-ink mt-6">{c.front}</div>
              <div className="text-sm text-brand-muted mt-8">Click to reveal</div>
            </div>
            <div className="flip-face flip-back absolute inset-0 bg-brand-ink text-brand-bone rounded-3xl shadow-card flex flex-col items-center justify-center p-12 text-center">
              <div className="text-xs uppercase tracking-[0.28em] text-brand-linen/60">Meaning</div>
              <div className="font-serif text-3xl sm:text-4xl mt-6 leading-tight">{c.back}</div>
              <div className="text-sm text-brand-linen/60 mt-8">Click to flip back</div>
            </div>
          </motion.div>
        </div>

        {currentState && (
          <div className="mt-4 text-[11px] uppercase tracking-widest text-brand-muted">
            Reps {currentState.reps} · Next {relativeDays(currentState.interval_days)}
          </div>
        )}

        <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
          <button data-testid={T.flashcardPrev} onClick={prev}
            className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-4 py-2 hover:bg-brand-linen text-sm">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Prev
          </button>
          <button data-testid={T.flashcardFlip} onClick={() => setFlipped(!flipped)}
            className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-2.5 hover:bg-brand-inkHover">
            <RotateCw className="w-4 h-4" strokeWidth={1.5} /> Flip
          </button>
          <button data-testid={T.flashcardNext} onClick={next}
            className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-4 py-2 hover:bg-brand-linen text-sm">
            Next <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {flipped && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 w-full max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-brand-muted mb-3 text-center">How well did you know it?</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {QUALITY_BUTTONS.map((b) => {
                const Icon = b.icon;
                return (
                  <button
                    key={b.label}
                    data-testid={`flashcard-rate-${b.label.toLowerCase()}`}
                    onClick={() => rate(b.quality)}
                    disabled={saving}
                    className={`px-3 py-3 border rounded-2xl text-sm inline-flex flex-col items-center gap-1 transition-colors disabled:opacity-60 ${b.tint}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.7} />
                    <span className="font-medium">{b.label}</span>
                    <span className="text-[10px] uppercase tracking-widest opacity-70">{b.hint}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
